/*

  Usage:

  const pageReducer = {
    initialState: {
      isFetching: false,
      items: [],
    },
    setFetchingStatus: (state, value) => ({ ...state, isFetching: value }),
    setItems: (state, items) => ({ ...state, items, isFetching: false }),
  }

  const Page = () => {
    const [ state, actions ] = useDispatchedReducer(pageReducer)

    const handleClick = () => {
      actions.setFetchingStatus(true)
    }

    return (
      <button onClick={handleClick}>fetch data</button>
    )
  }


  OR if you need initialState to be based on component props:


  const pageReducer = {
    initialState: {
      isFetching: false,
      items: [],
    },
    setFetchingStatus: (state, value) => ({ ...state, isFetching: value }),
    setItems: (state, items) => ({ ...state, items, isFetching: false }),
  }

  const modifyInitialState = (initialState) => ({
    ...initialState,
    items: initialItems,
  })

  const Page = ({ initialItems }) => {
    const [ state, actions ] = useDispatchedReducer(pageReducer, modifyInitialState)

    // ...
  }

 */

import React, { useCallback, useMemo, useReducer } from 'react'


/**
 *
 * @param {Object} methods
 * @param {Function} dispatch
 */
const dispatchMethods = (methods, dispatch) =>
  Object.keys(methods).reduce((result, methodName) => ({
    ...result,
    [methodName]: (payload) => dispatch({ type: methodName, payload }),
  }), {})

/**
 *
 * @param {Object} params
 * @param {Object} params.initialState
 * @param {Function} [modifyInitialState]
 * @return {[ Object, Object ]}
 */
const useDispatchedReducer = ({ initialState, ...methods }, modifyInitialState) => {
  const reducer = useCallback((state, { type, payload }) => methods[type](state, payload), []) // methods never change
  const [ state, dispatch ] = useReducer(reducer, initialState, modifyInitialState)

  const actions = useMemo(() => dispatchMethods(methods, dispatch), []) // methods never change

  return [ state, actions ]
}


export default useDispatchedReducer
